import chroma from 'chroma-js'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import editSrc from 'shared/assets/images/edit.svg'
import viewSrc from 'shared/assets/images/view.svg'
import { Tool } from 'shared/components/PageList/components/DndPages/components/Page/components/ContentPagePreview/Styles'
import { CoverSides } from 'shared/types/global'
import { ProjectDocument } from 'shared/types/layoutcreator/graphql'
import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import Text from 'shared/ui/Text'
import { usePdfPreviewImage } from 'shared/hooks/usePdfPreviewImage'

type ToolType = {
  id: ToolsEnum
  icon: string
}

enum ToolsEnum {
  edit = 'edit',
  view = 'view'
}

export const TOOLS: ToolType[] = [
  { id: ToolsEnum.edit, icon: editSrc },
  { id: ToolsEnum.view, icon: viewSrc }
]

const CoverImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const Overlay = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
  direction: 'column',
  rowGap: 8
})`
  opacity: 0;
  position: absolute;
  inset: 0;
  width: 100%;
  background: ${({ theme }) =>
    chroma(theme.color.base.c7).alpha(0.9).css()};
  color: ${({ theme }) => theme.color.base.c7};
  font-weight: 600;
  font-size: 10px;
`

const CoverContainer = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover ${Overlay} {
    opacity: 1;
  }
`

type CoverPageProps = {
  document: ProjectDocument
  side: CoverSides
  customCoverPreviewPath?: string
  onOpenPreview?: (
    document: ProjectDocument,
    side: CoverSides,
    customCoverPreviewPath?: string
  ) => void
}
export default function CoverPage({
  document,
  side,
  customCoverPreviewPath,
  onOpenPreview
}: CoverPageProps) {
  const navigate = useNavigate()
  const rawPreviewUrl = customCoverPreviewPath
    ? `${process.env.REACT_APP_REST_URL}${customCoverPreviewPath}`
    : document.previewUrl
  const { previewUrl, isLoading } = usePdfPreviewImage(rawPreviewUrl, {
    pageNumber: 1,
    scale: 1.5
  })
  const showSkeleton = Boolean(rawPreviewUrl) && (isLoading || !previewUrl)

  const actions: Record<ToolsEnum, (doc: ProjectDocument) => void> = {
    [ToolsEnum.edit]: doc => navigate(`editor/${side}`),
    [ToolsEnum.view]: doc =>
      onOpenPreview?.(doc, side, customCoverPreviewPath)
  }

  return (
    <Flex direction="column" key={document.id} width="100%" height="100%">
      <CoverContainer>
        {showSkeleton ? (
          <Skeleton height="100%" width="100%" radius={8} />
        ) : (
          <CoverImage src={previewUrl || ''} alt={document.name} />
        )}
        <Overlay>
          <Flex columnGap={16}>
            {TOOLS.map(tool => (
              <Tool
                data-testid={`CoverPageTool:${document.id}:${tool.id}`}
                key={tool.id}
                onClick={() => actions[tool.id](document)}
              >
                <img
                  src={tool.icon}
                  alt={tool.id}
                  style={{ width: 64 }}
                />
              </Tool>
            ))}
          </Flex>
        </Overlay>
      </CoverContainer>
      <Text
        tag="p"
        align="center"
        id={
          document.name.includes('inside')
            ? 'Cover.inside'
            : 'Cover.outside'
        }
        color={theme => theme.color.base.c7}
        weight={600}
        size={12}
        marginTop={24}
      />
    </Flex>
  )
}
